/* * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license. * See LICENSE in the project root for license information. */

/* global document, Office */

import { inject } from "@vercel/analytics"

// Vercel Web Analytics: zählt Aufrufe des Task Panes
inject()

// Global variables
var allFiles = []
var isOfficeReady = false
var isDOMReady = false
var isInitialized = false
var debugMode = true // Enable debug mode for more logging
var isLoggedOut = false // Client-side logout flag
var blobUploadManager = null // Will be initialized later
var activeUploads = new Map() // Track active uploads
var currentUploadCard = null // Track current upload card

// Zentrale Backend-URL: build-time aus .env (BACKEND_URL), siehe webpack.config.js. Kein Fallback.
var BACKEND_URL = process.env.BACKEND_URL

// Hilfsfunktion: Gibt die Backend-URL zurück (konsistent mit blobUploadManager)
function getBackendUrl() {
  try {
    // Laufzeit-Override für lokales Testing: localStorage.setItem("UPLOAD_API_BASE", "http://localhost:4000")
    var explicit = window.localStorage && window.localStorage.getItem("UPLOAD_API_BASE")
    if (explicit) return explicit
  } catch (e) {}
  return BACKEND_URL
}

// Declare Office variable
var Office = window.Office

// Debug function to log messages only when debug mode is on
function debugLog() {
  if (debugMode && console && console.log) {
    console.log.apply(console, arguments)
    // Also add to debug div if it exists
    var debugDiv = document.getElementById("debug-output")
    if (debugDiv) {
      var message = Array.prototype.slice.call(arguments).join(" ")
      var entry = document.createElement("div")
      entry.textContent = new Date().toLocaleTimeString() + ": " + message
      debugDiv.appendChild(entry)
      // Limit entries
      while (debugDiv.children.length > 50) {
        debugDiv.removeChild(debugDiv.firstChild)
      }
      // Auto-scroll to bottom
      debugDiv.scrollTop = debugDiv.scrollHeight
    }
  }
}

// Polyfill für fetch in älteren Outlook-Versionen
if (!window.fetch) {
  window.fetch = (url, options) =>
    new Promise((resolve, reject) => {
      var xhr = new XMLHttpRequest()
      xhr.open(options.method || "GET", url)
      if (options.headers) {
        for (var key in options.headers) {
          xhr.setRequestHeader(key, options.headers[key])
        }
      }
      xhr.onload = () => {
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          json: () => Promise.resolve(JSON.parse(xhr.responseText)),
          text: () => Promise.resolve(xhr.responseText),
        })
      }
      xhr.onerror = () => {
        reject(new Error("Network error"))
      }
      xhr.ontimeout = () => {
        reject(new Error("Request timeout"))
      }
      xhr.send(options.body)
    })
}

// Polyfill for remove() method
if (!Element.prototype.remove) {
  Element.prototype.remove = function () {
    if (this.parentNode) {
      this.parentNode.removeChild(this)
    }
  }
}

// Office ready callback
Office.onReady((info) => {
  debugLog("Office is ready:", info.host)
  isOfficeReady = true
  if (info.host === Office.HostType.Outlook) {
    tryInitialize()
  }
})

// DOM ready callback
document.addEventListener("DOMContentLoaded", () => {
  debugLog("DOM is ready")
  isDOMReady = true
  tryInitialize()
})

// Try to initialize when both conditions are met
function tryInitialize() {
  if (isOfficeReady && isDOMReady && !isInitialized) {
    debugLog("Both Office and DOM are ready, initializing app...")
    isInitialized = true
    initializeApp()
  }
}

// Initialize app when both Office and DOM are ready
function initializeApp() {
  debugLog("App initialization started")
  // Check if required elements exist
  var sideloadMsg = document.getElementById("sideload-msg")
  var appBody = document.getElementById("app-body")
  var runButton = document.getElementById("run")

  if (sideloadMsg) {
    sideloadMsg.style.display = "none"
  }
  if (appBody) {
    appBody.style.display = "flex"
  }
  if (runButton) {
    runButton.onclick = run
  }

  // Initialize the main application
  initializeMainApp()
}

function run() {
  debugLog("Run function called")
  try {
    var item = Office.context.mailbox.item
    var insertAt = document.getElementById("item-subject")
    if (insertAt && item) {
      var label = document.createElement("b")
      label.appendChild(document.createTextNode("Subject: "))
      insertAt.appendChild(document.createElement("br"))
      insertAt.appendChild(document.createTextNode(item.subject))
      insertAt.appendChild(document.createElement("br"))
    }
  } catch (error) {
    debugLog("Error in run function:", error)
  }
}

function initializeMainApp() {
  debugLog("Main app initialization started")

  // Initialize BlobUploadManager
  var BlobUploadManager = window.BlobUploadManager // Declare BlobUploadManager variable
  if (typeof BlobUploadManager !== "undefined") {
    blobUploadManager = new BlobUploadManager()
    debugLog("BlobUploadManager initialized")
  } else {
    debugLog("BlobUploadManager not available, falling back to regular upload")
  }

  // Get DOM elements with null checks
  var loginScreen = document.getElementById("loginScreen")
  var appContainer = document.getElementById("mainApp")
  var tokenSubmit = document.getElementById("tokenSubmit")
  var tokenInput = document.getElementById("tokenInput")
  var loginMessage = document.getElementById("loginMessage")

  debugLog("DOM elements found:", {
    loginScreen: !!loginScreen,
    appContainer: !!appContainer,
    tokenSubmit: !!tokenSubmit,
    tokenInput: !!tokenInput,
    loginMessage: !!loginMessage,
  })

  // Check if required elements exist
  if (!loginScreen || !appContainer || !tokenSubmit || !tokenInput || !loginMessage) {
    debugLog("Required DOM elements not found")
    return
  }

  // Create and show initial loader
  var initialLoader = document.createElement("div")
  initialLoader.id = "initialLoader"
  initialLoader.style.cssText =
    "position: fixed;" +
    "top: 0; left: 0; right: 0; bottom: 0;" +
    "display: flex;" +
    "align-items: center;" +
    "justify-content: center;" +
    "background: white;" +
    "z-index: 9999;" +
    "font-family: 'Inter', sans-serif;"

  initialLoader.innerHTML =
    '<div style="display: flex; flex-direction: column; align-items: center; gap: 12px;">' +
    '<div class="floating-icon">📁</div>' +
    '<div class="loading-bar-container">' +
    '<div class="loading-bar"></div>' +
    "</div>" +
    '<div class="loading-text">anolink wird geladen</div>' +
    "</div>"

  document.body.appendChild(initialLoader)

  // Initially hide both screens
  loginScreen.style.display = "none"
  appContainer.style.display = "none"

  function checkLocalTokenAndShow() {
    debugLog("Checking local token...")
    // Prüfe zuerst die client-seitige Logout-Flag
    var currentEmail = null
    try {
      currentEmail = window.Office.context.mailbox.userProfile.emailAddress
    } catch (e) {
      debugLog("Could not get email for logout check")
    }

    // Prüfe localStorage für Logout-Status
    var loggedOutFromStorage = false
    var loggedOutEmail = null
    try {
      loggedOutFromStorage = localStorage.getItem("anolink_logged_out") === "true"
      loggedOutEmail = localStorage.getItem("anolink_logout_email")
      debugLog("Logout status from storage:", loggedOutFromStorage, "for email:", loggedOutEmail)
    } catch (e) {
      debugLog("localStorage not available for logout check:", e)
    }

    // Wenn ausgeloggt und gleiche E-Mail, zeige Login-Screen
    if ((isLoggedOut || loggedOutFromStorage) && currentEmail === loggedOutEmail) {
      debugLog("User is logged out, showing login screen")
      showLoginScreen()
      return
    }

    try {
      // Prüfung für Outlook 2019 Kompatibilität
      if (
        !window.Office ||
        !window.Office.context ||
        !window.Office.context.mailbox ||
        !window.Office.context.mailbox.userProfile
      ) {
        debugLog("Office-Kontext nicht verfügbar, zeige Login-Screen")
        showLoginScreen()
        return
      }

      var email = window.Office.context.mailbox.userProfile.emailAddress
      if (!email) {
        debugLog("E-Mail-Adresse nicht verfügbar, zeige Login-Screen")
        showLoginScreen()
        return
      }

      debugLog("Checking for locally stored token for email:", email)

      // Prüfe lokalen Token
      var localToken = null
      var localEmail = null
      try {
        localToken = localStorage.getItem("anolink_token")
        localEmail = localStorage.getItem("anolink_email")
        debugLog("Local token found:", !!localToken, "for email:", localEmail)
      } catch (e) {
        debugLog("No local storage available")
        showLoginScreen()
        return
      }

      // Wenn lokaler Token für diese E-Mail existiert, validiere ihn
      if (localToken && localEmail === email) {
        debugLog("Validating local token...")
        var backendUrl = getBackendUrl()
        var verifyUrl = backendUrl + "/verifyOutlookToken"
        debugLog("Calling verifyOutlookToken at:", verifyUrl)
        // Kein Authorization-Bearer mehr: das Backend prüft die Kombination
        // (email, token) direkt in der outlook_addin_tokens-Tabelle und ist
        // gegen Brute-Force per Ratelimit geschützt.
        fetch(verifyUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Client-Type": "outlook-addin",
          },
          body: JSON.stringify({ email: email, token: localToken }),
        })
          .then(async (response) => {
            debugLog("Local token validation response status:", response.status, "URL:", verifyUrl)
            var data = null
            try {
              data = await response.json()
            } catch (parseErr) {
              debugLog("JSON parse error:", parseErr, "Status:", response.status)
              // Wenn 404, dann Route nicht gefunden
              if (response.status === 404) {
                throw new Error("API-Endpoint nicht gefunden (404). Route: " + verifyUrl)
              }
              throw new Error("Ungültige Serverantwort. Status: " + response.status + ", URL: " + verifyUrl)
            }
            return data
          })
          .then((data) => {
            debugLog("Local token validation response:", JSON.stringify(data))
            if (data.success) {
              debugLog("Local token is valid, showing app")
              // Zeige App
              appContainer.style.display = "flex"
              appContainer.style.flexDirection = "column"
              appContainer.style.width = "100%"
              initializeFileUpload()
            } else {
              debugLog("Local token is invalid, removing it and showing login")
              try {
                localStorage.removeItem("anolink_token")
                localStorage.removeItem("anolink_email")
              } catch (e) {}
              showLoginScreen()
            }
          })
          .catch((err) => {
            debugLog("Local token validation failed:", err)
            showLoginScreen()
          })
          .finally(() => {
            removeLoader()
          })
      } else {
        // Kein lokaler Token oder falsche E-Mail
        debugLog("No valid local token found, showing login")
        showLoginScreen()
      }
    } catch (err) {
      debugLog("Token validation error:", err)
      showLoginScreen()
    }
  }

  function showLoginScreen() {
    debugLog("Showing login screen")
    if (loginScreen) {
      loginScreen.style.display = "flex"
    }
    if (appContainer) {
      appContainer.style.display = "none"
    }
    removeLoader()
  }

  function removeLoader() {
    var loader = document.getElementById("initialLoader")
    if (loader && loader.parentNode) {
      loader.parentNode.removeChild(loader)
    }
  }

  // Token submit event listener
  debugLog("Adding token submit event listener")
  tokenSubmit.addEventListener("click", handleTokenSubmit)

  function handleTokenSubmit() {
    debugLog("Token submit clicked")
    var token = tokenInput.value.trim()

    // Button deaktivieren und Text ändern
    tokenSubmit.disabled = true
    tokenSubmit.innerHTML = '<span class="loading-spinner"></span>Wird geprüft...'
    loginMessage.textContent = ""

    // Funktion zum Zurücksetzen des Buttons
    function resetButton() {
      tokenSubmit.disabled = false
      tokenSubmit.innerHTML = "Einloggen"
    }

    if (!token) {
      loginMessage.textContent = "❌ Bitte Token eingeben."
      resetButton()
      return
    }

    try {
      if (
        !window.Office ||
        !window.Office.context ||
        !window.Office.context.mailbox ||
        !window.Office.context.mailbox.userProfile
      ) {
        throw new Error("Office-Kontext nicht verfügbar. Bitte stellen Sie sicher, dass das Add-In in Outlook läuft.")
      }

      var email = window.Office.context.mailbox.userProfile.emailAddress
      if (!email) {
        throw new Error("E-Mail-Adresse konnte nicht abgerufen werden.")
      }

      debugLog("Checking token for email:", email, "with token:", token)
      var backendUrl = getBackendUrl()
      var verifyUrl = backendUrl + "/verifyOutlookToken"
      debugLog("Calling verifyOutlookToken at:", verifyUrl)

      // Kein Authorization-Bearer mehr: die frühere API_KEY-Schicht wurde
      // entfernt (der Key lag im Add-In-Bundle offen). Das Backend prüft die
      // Kombination (email, token) in der outlook_addin_tokens-Tabelle und
      // ist gegen Brute-Force per Ratelimit geschützt.
      fetch(verifyUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Client-Type": "outlook-addin",
        },
        body: JSON.stringify({ email: email, token: token }),
      })
        .then(async (response) => {
          debugLog("Token validation response status:", response.status, "URL:", verifyUrl)
          var data = null
          try {
            data = await response.json()
          } catch (parseErr) {
            debugLog("JSON parse error:", parseErr, "Status:", response.status)
            // Wenn 404, dann Route nicht gefunden
            if (response.status === 404) {
              throw new Error("API-Endpoint nicht gefunden (404). Route: " + verifyUrl)
            }
            throw new Error("Ungültige Serverantwort. Status: " + response.status + ", URL: " + verifyUrl)
          }
          if (!response.ok) {
            var errorMsg = data && data.error ? data.error : "Token-Validierung fehlgeschlagen"
            debugLog("Token validation failed:", errorMsg, "Status:", response.status, "URL:", verifyUrl)
            if (response.status === 404) {
              throw new Error("API-Endpoint nicht gefunden (404). Route: " + verifyUrl)
            }
            throw new Error(errorMsg + " (Status: " + response.status + ", URL: " + verifyUrl + ")")
          }
          return data
        })
        .then((data) => {
          debugLog("Token validation response data:", JSON.stringify(data))
          if (data.success) {
            debugLog("Token valid, saving locally and showing app")
            // TOKEN LOKAL SPEICHERN
            try {
              localStorage.setItem("anolink_token", token)
              localStorage.setItem("anolink_email", email)
              debugLog("Token saved locally successfully")
            } catch (saveErr) {
              debugLog("Failed to save token locally:", saveErr)
              // Trotzdem weitermachen, auch wenn Speichern fehlschlägt
            }

            // Logout-Flags zurücksetzen bei erfolgreichem Login
            isLoggedOut = false
            try {
              localStorage.removeItem("anolink_logged_out")
              localStorage.removeItem("anolink_logout_email")
              debugLog("Logout flags cleared")
            } catch (e) {
              debugLog("Could not clear logout flags:", e)
            }

            loginScreen.style.display = "none"
            // Explizit CSS-Eigenschaften setzen für korrektes Layout
            appContainer.style.display = "flex"
            appContainer.style.flexDirection = "column"
            appContainer.style.width = "100%"

            // Initialize file upload after successful login
            initializeFileUpload()

            // Bei Erfolg Button NICHT zurücksetzen, da wir zur App wechseln
          } else {
            loginMessage.textContent = "❌ Token ungültig oder nicht autorisiert."
            resetButton()
          }
        })
        .catch((err) => {
          debugLog("Login-Fehler:", err)
          loginMessage.textContent = "❌ " + (err.message || "Serverfehler. Bitte später erneut versuchen.")
          resetButton()
        })
    } catch (err) {
      debugLog("Login-Fehler:", err)
      loginMessage.textContent = "❌ " + (err.message || "Serverfehler. Bitte später erneut versuchen.")
      resetButton()
    }
  }

  // Enter key listener for token input
  tokenInput.addEventListener("keyup", (e) => {
    if (e.key === "Enter") {
      handleTokenSubmit()
    }
  })

  // Start token validation with delay for Outlook 2019
  setTimeout(() => {
    checkLocalTokenAndShow()
  }, 1000)
}

function initializeFileUpload() {
  debugLog("Initializing file upload...")

  var uploadArea = document.getElementById("uploadArea")
  var uploadButton = document.getElementById("uploadButton")
  var fileInput = document.getElementById("fileInput")
  var folderInput = document.getElementById("folderInput")
  var folderSelectLink = document.getElementById("folderSelectLink")
  // Ordner-UI
  var folderSection = document.getElementById("folderSection")
  var addFolderBtn = document.getElementById("addFolderBtn")
  var folderList = document.getElementById("folderList")
  var uploadTextEl = uploadArea ? uploadArea.querySelector('.upload-text') : null
  var uploadSubtextEl = uploadArea ? uploadArea.querySelector('.upload-subtext') : null
  var fileList = document.getElementById("fileList")
  var fileListContainer = document.getElementById("fileListContainer")
  var fileCount = document.getElementById("fileCount")
  var messageBox = document.getElementById("message")
  // Backup-Bereich: E-Mail-Text zum manuellen Kopieren
  var mailFallback = document.getElementById("mailFallback")
  var mailFallbackContent = document.getElementById("mailFallbackContent")
  var copyMailText = document.getElementById("copyMailText")

  debugLog("File upload elements found:", {
    uploadArea: !!uploadArea,
    uploadButton: !!uploadButton,
    fileInput: !!fileInput,
    folderInput: !!folderInput,
    folderSelectLink: !!folderSelectLink,
    folderSection: !!folderSection,
    addFolderBtn: !!addFolderBtn,
    folderList: !!folderList,
    uploadTextEl: !!uploadTextEl,
    uploadSubtextEl: !!uploadSubtextEl,
    fileList: !!fileList,
    fileListContainer: !!fileListContainer,
    fileCount: !!fileCount,
    messageBox: !!messageBox,
  })

  // Hilfsfunktion: Ordnerpfad = nur aktueller Ordnername
  function getFolderPathFrom(container) {
    try {
      var input = container.querySelector('input[type="text"]')
      return (input && input.value) ? input.value : 'Ordner'
    } catch (e) { return 'Ordner' }
  }

  // UI-Builder: erzeugt eine Ordner-Kachel. parentContainer: DOM-Element, in das der Ordner eingefügt wird
  function createFolderItem(parentContainer) {
    var folderId = 'f_' + Date.now() + '_' + Math.floor(Math.random() * 1000000)
    var container = document.createElement('div')
    container.className = 'folder-item'
    container.style.cssText = 'border:1px solid #e5e7eb;padding:8px;border-radius:8px;margin-bottom:10px;position:relative;'
    container.setAttribute('data-folder-id', folderId)

    var header = document.createElement('div')
    header.style.cssText = 'display:flex;align-items:center;justify-content:flex-start;margin-bottom:6px;gap:8px;'
    var nameInput = document.createElement('input')
    nameInput.type = 'text'
    nameInput.placeholder = 'Ordnername'
    nameInput.style.cssText = 'font-size:12px;padding:4px 8px;border:1px solid #ddd;border-radius:6px;width:200px;'

    // Kleines rotes X oben rechts
    var closeBtn = document.createElement('div')
    closeBtn.textContent = '×'
    closeBtn.title = 'Ordner entfernen'
    closeBtn.style.cssText = 'position:absolute;right:8px;top:6px;color:#ef4444;font-weight:700;cursor:pointer;line-height:1;'
    closeBtn.onclick = function () {
      if (container && container.parentNode) container.parentNode.removeChild(container)
    }

    // Dateien hinzufügen Button unter dem Namen (links)
    var controls = document.createElement('div')
    controls.style.cssText = 'margin:6px 0;display:flex;gap:8px;flex-wrap:wrap;'
    var addFilesBtn = document.createElement('button')
    addFilesBtn.className = 'button'
    addFilesBtn.style.cssText = 'padding:3px 6px;font-size:11px;line-height:1.2;'
    addFilesBtn.textContent = 'Dateien'

    var uploadFolderBtn = document.createElement('button')
    uploadFolderBtn.className = 'button'
    uploadFolderBtn.style.cssText = 'padding:3px 6px;font-size:11px;line-height:1.2;'
    uploadFolderBtn.textContent = 'Ordner hochladen'

    controls.appendChild(addFilesBtn)
    controls.appendChild(uploadFolderBtn)
    // Unterordner entfernt

    var filesUl = document.createElement('ul')
    // Hinweis: flex-gap wird in älteren WebViews nicht unterstützt → zusätzlich Margins auf <li>
    filesUl.style.cssText = 'list-style:none;margin:0;padding:0;display:flex;flex-wrap:wrap;gap:12px;'

    var hiddenInput = document.createElement('input')
    hiddenInput.type = 'file'
    hiddenInput.multiple = true
    hiddenInput.style.display = 'none'
    container.appendChild(hiddenInput)

    // Pending-Dateien für diesen Ordner sammeln
    var pendingFiles = []
    try { container._pendingFiles = pendingFiles } catch (e) {}
    try { container._folderId = folderId } catch (e) {}

    header.appendChild(nameInput)
    container.appendChild(header)
    container.appendChild(controls)
    container.appendChild(filesUl)
    container.appendChild(closeBtn)
    parentContainer.appendChild(container)

    // Renderer für Pending-Dateien mit Entfernen-Button pro Eintrag
    function renderPending() {
      // Upload-Button aktivieren/deaktivieren
      uploadFolderBtn.disabled = pendingFiles.length === 0

      while (filesUl.firstChild) filesUl.removeChild(filesUl.firstChild)
      for (var p = 0; p < pendingFiles.length; p++) {
        ;(function (idx) {
          var pf = pendingFiles[idx]
          var li = document.createElement('li')
          li.style.cssText = 'font-size:12px;background:#f9fafb;border:1px solid #eee;border-radius:6px;padding:6px 8px;margin:8px 8px 0 0;display:flex;align-items:center;gap:12px;'

          var nameSpan = document.createElement('span')
          nameSpan.textContent = pf && pf.name ? pf.name : 'Datei'
          li.appendChild(nameSpan)

          var removeBtn = document.createElement('span')
          removeBtn.textContent = '×'
          removeBtn.title = 'Datei entfernen'
          removeBtn.style.cssText = 'color:#ef4444;font-weight:700;cursor:pointer;line-height:1;margin-left:8px;'
          removeBtn.onclick = function () {
            try { pendingFiles.splice(idx, 1) } catch (e) {}
            renderPending()
          }
          li.appendChild(removeBtn)

          filesUl.appendChild(li)
        })(p)
      }
    }

    addFilesBtn.onclick = function () { try { hiddenInput.click() } catch (e) {} }
    hiddenInput.onchange = function (e) {
      var files = e && e.target && e.target.files ? e.target.files : null
      if (!files || !files.length) return
      for (var i = 0; i < files.length; i++) {
        var f = files[i]
        try { f.folderId = container._folderId } catch (e) {}
        pendingFiles.push(f)
      }
      hiddenInput.value = ''
      debugLog('Ordner-Pending-Dateien:', pendingFiles.length)
      renderPending()
    }

    // Initial render
    renderPending()

    uploadFolderBtn.onclick = async function () {
      if (!pendingFiles.length) {
        showMessage('error', 'Keine Dateien im Ordner. Bitte fügen Sie Dateien hinzu.')
        return
      }
      uploadFolderBtn.disabled = true
      var label = getFolderPathFrom(container)
      try {
        // Sequenziell hochladen, um die WebView nicht zu überlasten
        for (var i = 0; i < pendingFiles.length; i++) {
          var f = pendingFiles[i]
          try { f.folderLabel = label } catch (e) {}
          try { f.folderId = container._folderId } catch (e) {}
          if (blobUploadManager) {
            await uploadFileWithChunks(f)
          } else {
            allFiles.push({ name: f.name, size: f.size, isUploaded: false, folderLabel: label, folderId: container._folderId })
          }
        }
        // Aufräumen: Pending leeren und Editor-Kachel entfernen (Ordner nur unten anzeigen)
        pendingFiles.length = 0
        try { if (container && container.parentNode) container.parentNode.removeChild(container) } catch (e) {}
        renderFiles()
        showMessage('success', 'Ordner erfolgreich hochgeladen.')
      } catch (err) {
        showMessage('error', 'Fehler beim Ordner-Upload: ' + (err && err.message ? err.message : 'Unbekannter Fehler'))
      } finally {
        uploadFolderBtn.disabled = false
      }
    }

    // Unterordner-Logik entfernt
  }
  // Ordner hinzufügen UI
  if (addFolderBtn && folderList) {
    addFolderBtn.onclick = function () { createFolderItem(folderList) }
  }

  // Check if elements exist
  if (!uploadArea || !uploadButton || !fileInput || !fileList || !fileListContainer || !fileCount || !messageBox) {
    debugLog("File upload elements not found")
    return
  }

  function renderFiles() {
    // Angepasste Darstellung: Dateien, die bereits in einem Ordner verwaltet werden, nicht doppelt anzeigen
    var visibleFiles = []
    var folderGroups = {}
    for (var i = 0; i < allFiles.length; i++) {
      var f = allFiles[i]
      if (f.folderLabel) {
        if (f.isUploaded) folderGroups[f.folderLabel] = true
      } else {
        visibleFiles.push(f)
      }
    }
    debugLog("Rendering files (visible only):", visibleFiles.length)

    while (fileList.firstChild) fileList.removeChild(fileList.firstChild)
    messageBox.className = ""
    messageBox.innerHTML = ""

    if (visibleFiles.length > 0 || Object.keys(folderGroups).length > 0) {
      fileListContainer.classList.add("has-files")
      fileCount.textContent = "(" + (visibleFiles.length + Object.keys(folderGroups).length) + ")"

      // Zuerst: fertige Ordner als Boxen
      var groupLabels = Object.keys(folderGroups)
      for (var g = 0; g < groupLabels.length; g++) {
        (function () {
          var label = groupLabels[g]
          var li = document.createElement("li")
          li.style.cssText = 'border:1px solid #e5e7eb;padding:8px;border-radius:8px;margin-bottom:10px;display:flex;align-items:center;justify-content:space-between;background:#fff;'
          var left = document.createElement('div')
          left.style.cssText = 'display:flex;align-items:center;gap:10px;'
          var folderIcon = document.createElement('span')
          folderIcon.textContent = '📁'
          folderIcon.style.cssText = 'font-size:16px;margin-right:8px;'
          var nameEl = document.createElement('div')
          nameEl.style.cssText = 'font-weight:600;'
          nameEl.textContent = label
          left.appendChild(folderIcon)
          left.appendChild(nameEl)
          var remove = document.createElement('div')
          remove.textContent = '×'
          remove.title = 'Ordner entfernen'
          remove.style.cssText = 'color:#ef4444;font-weight:700;cursor:pointer;line-height:1;'
          remove.onclick = function () {
            for (var a = allFiles.length - 1; a >= 0; a--) {
              if (allFiles[a].folderLabel === label) allFiles.splice(a, 1)
            }
            renderFiles()
          }
          li.appendChild(left)
          li.appendChild(remove)
          fileList.appendChild(li)
        })()
      }

      // Danach: einzelne Dateien ohne Ordner als Boxen
      for (var j = 0; j < visibleFiles.length; j++) {
        var file = visibleFiles[j]
        var li = document.createElement("li")
        li.style.cssText = 'border:1px solid #e5e7eb;padding:8px;border-radius:8px;margin-bottom:10px;display:flex;align-items:center;justify-content:space-between;background:#fff;'

        var left = document.createElement('div')
        left.style.cssText = 'display:flex;align-items:center;gap:10px;'
        var fileIcon = document.createElement('span')
        fileIcon.textContent = '📄'
        fileIcon.style.cssText = 'font-size:16px;margin-right:8px;'
        var titleWrap = document.createElement('div')
        var name = document.createElement('div')
        name.style.cssText = 'font-weight:600;'
        name.textContent = file.name.length > 30 ? file.name.substring(0, 28) + '...' : file.name
        name.title = file.name
        titleWrap.appendChild(name)
        left.appendChild(fileIcon)
        left.appendChild(titleWrap)

        var remove = document.createElement('div')
        remove.textContent = '×'
        remove.title = 'Datei entfernen'
        remove.style.cssText = 'color:#ef4444;font-weight:700;cursor:pointer;line-height:1;'
        var idxInAll = -1
        for (var k = 0; k < allFiles.length; k++) { if (allFiles[k] === file) { idxInAll = k; break } }
        remove.setAttribute('data-index', idxInAll)
        remove.onclick = removeFile

        li.appendChild(left)
        li.appendChild(remove)
        fileList.appendChild(li)
      }
    } else {
      fileListContainer.classList.remove("has-files")
      fileCount.textContent = "(0)"
    }
  }

  function formatSize(bytes) {
    if (bytes === 0) return "0 Bytes"
    var k = 1024
    var sizes = ["Bytes", "KB", "MB", "GB"]
    var i = Math.floor(Math.log(bytes) / Math.log(k))
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  function removeFile(e) {
    debugLog("Remove file clicked")
    e = e || window.event
    e.stopPropagation()
    var index = Number.parseInt(this.getAttribute("data-index"), 10)
    debugLog("Removing file at index:", index)
    allFiles.splice(index, 1)
    renderFiles()
  }

  function appendFiles(newFiles) {
    debugLog("Appending files, count:", newFiles.length)

    var existing = {}
    for (var i = 0; i < allFiles.length; i++) {
      existing[allFiles[i].name + "_" + allFiles[i].size] = true
    }

    // Normalisieren zu Array und setze webkitRelativePath falls vorhanden
    var list = Array.prototype.slice.call(newFiles)
    for (var j = 0; j < list.length; j++) {
      var file = list[j]
      debugLog("Processing file:", file.name, file.size)

      if (!existing[file.name + "_" + file.size]) {
        // Start chunked upload immediately
        if (blobUploadManager) {
          uploadFileWithChunks(file)
        } else {
          // Fallback to adding to list for regular upload
          allFiles.push(file)
        }
        debugLog("Added file for upload")
      } else {
        debugLog("File already exists, skipping")
      }
    }

    if (!blobUploadManager) {
      debugLog("Total files after append:", allFiles.length)
      renderFiles()
    }
  }

  // Ordner-Rekursionshilfe: liest Dateien aus DataTransferItemList
  function getFilesFromDataTransferItems(items) {
    return new Promise(function (resolve, reject) {
      try {
        var entries = []
        for (var i = 0; i < items.length; i++) {
          var it = items[i]
          var entry = it.webkitGetAsEntry && it.webkitGetAsEntry()
          if (!entry && typeof it.getAsFile === 'function') {
            // Manche Runtimes liefern keine Entry-API – dann normale Files nutzen
            var f = it.getAsFile && it.getAsFile()
            if (f) entries.push({ isFile: true, file: function (cb) { cb(f) } })
          } else if (entry) {
            entries.push(entry)
          }
        }
        var files = []
        var pending = 0

        function readEntry(entry, path) {
          if (entry.isFile) {
            pending++
            entry.file(function (file) {
              try {
                Object.defineProperty(file, 'webkitRelativePath', { value: path + file.name })
              } catch (e) {}
              files.push(file)
              pending--
              if (pending === 0) resolve(files)
            }, function () { pending--; if (pending === 0) resolve(files) })
          } else if (entry.isDirectory) {
            var reader = entry.createReader()
            pending++
            reader.readEntries(function (entries) {
              for (var k = 0; k < entries.length; k++) {
                readEntry(entries[k], path + entry.name + "/")
              }
              pending--
              if (pending === 0) resolve(files)
            }, function () { pending--; if (pending === 0) resolve(files) })
          }
        }

        if (!entries.length) return resolve([])
        for (var j = 0; j < entries.length; j++) readEntry(entries[j], "")
      } catch (e) {
        reject(e)
      }
    })
  }

  function uploadFileWithChunks(file) {
    debugLog("Starting chunked upload for:", file.name, formatSize(file.size))
    var uploadId = file.name + "_" + Date.now()
    activeUploads.set(uploadId, { file: file, status: "uploading" })

    // SOFORT Upload-Status-Kachel anzeigen
    createAndShowUploadCard(file)

    // E-Mail-Adresse an File-Objekt anheften (für Backend, um orgId/userId zu holen)
    try {
      if (window.Office && window.Office.context && window.Office.context.mailbox && window.Office.context.mailbox.userProfile) {
        var email = window.Office.context.mailbox.userProfile.emailAddress
        if (email) {
          file.accountName = email
          debugLog("Email address attached to file:", email)
        }
      }
    } catch (e) {
      debugLog("Could not get email address:", e)
    }

    // Ordnerlabel, falls im UI gesetzt wurde, an File-Objekt anheften
    try {
      if (file && typeof file === 'object' && file.folderLabel == null) {
        // Versuche das Label aus bereits hinzugefügten Dateien zu lesen (gleicher Name/Größe)
        for (var i = 0; i < allFiles.length; i++) {
          if (allFiles[i].name === file.name && allFiles[i].size === file.size && allFiles[i].folderLabel) {
            file.folderLabel = allFiles[i].folderLabel
            break
          }
        }
      }
    } catch (e) {}

    blobUploadManager
      .uploadFile(file, (progress) => {
        debugLog("🔥 PROGRESS UPDATE:", progress.fileName, progress.progress + "%", progress.phase, progress.status)
        updateUploadStatusCard(progress)
      })
      .then((result) => {
        debugLog("Chunked upload completed:", result)
        // Add to allFiles with the blob URL
        allFiles.push({
          name: result.originalFileName || result.fileName,
          size: result.size || file.size,
          blobUrl: result.blobUrl,
          isUploaded: true,
          folderLabel: file.folderLabel || "",
        })
        activeUploads.delete(uploadId)
        renderFiles()

        // Hide upload card after success
        setTimeout(() => {
          hideUploadStatusCard()
        }, 2000)
      })
      .catch((error) => {
        debugLog("Chunked upload failed:", error)
        activeUploads.delete(uploadId)
        showMessage("error", "Upload fehlgeschlagen: " + error.message)

        // Show error state in card
        showUploadStatusCardError(file, error.message)

        // Hide upload card after error
        setTimeout(() => {
          hideUploadStatusCard()
        }, 3000)
      })
  }

  // NEUE FUNKTION: Erstelle Upload-Kachel direkt im DOM
  function createAndShowUploadCard(file) {
    debugLog("Creating upload status card for:", file.name)

    // Entferne alte Kachel falls vorhanden
    var existingCard = document.getElementById("uploadStatusCard")
    if (existingCard && existingCard.parentNode) {
      existingCard.parentNode.removeChild(existingCard)
      debugLog("Removed existing upload card")
    }

    var ext = file.name.split(".").pop().toLowerCase().toUpperCase()
    var filename = file.name.length > 25 ? file.name.substring(0, 22) + "..." : file.name

    // Erstelle neue Kachel
    var card = document.createElement("div")
    card.id = "uploadStatusCard"
    card.className = "upload-status-card"
    card.style.display = "flex" // Wichtig: Sofort sichtbar machen!

    card.innerHTML = `
      <div class="upload-status-icon" id="uploadStatusIcon">
        <div class="file-type-badge" id="fileTypeBadge">${ext}</div>
      </div>
      <div class="upload-status-content">
        <div class="upload-status-filename" id="uploadStatusFilename">${filename}</div>
        <div class="upload-status-progress" id="uploadStatusProgress">1%</div>
      </div>
      <div class="upload-status-indicator" id="uploadStatusIndicator">
        <div class="progress-ring">
          <svg class="progress-ring-svg" width="24" height="24">
            <circle class="progress-ring-circle-bg" cx="12" cy="12" r="10"></circle>
            <circle class="progress-ring-circle" cx="12" cy="12" r="10" id="progressCircle"></circle>
          </svg>
        </div>
      </div>
    `

    // Füge Kachel nach der Upload-Box ein
    var uploadArea = document.getElementById("uploadArea")
    if (uploadArea && uploadArea.parentNode) {
      uploadArea.parentNode.insertBefore(card, uploadArea.nextSibling)
      debugLog("Upload status card inserted into DOM after upload area")
    } else {
      debugLog("Could not find upload area to insert card")
      // Fallback: Füge zur upload-section hinzu
      var uploadSection = document.querySelector(".upload-section")
      if (uploadSection) {
        uploadSection.appendChild(card)
        debugLog("Upload status card appended to upload section as fallback")
      }
    }

    currentUploadCard = { file: file, status: "uploading" }

    // Initial progress ring setup
    updateProgressRing(1)
    debugLog("Upload status card created and should be visible now")
  }

  // FIXED: Bessere Progress-Updates
  function updateUploadStatusCard(progress) {
    var card = document.getElementById("uploadStatusCard")
    var progressText = document.getElementById("uploadStatusProgress")
    if (!card || !progressText) {
      debugLog("Upload status card elements not found for update")
      return
    }

    // BESSERE PHASE-ANZEIGE
    var displayText = progress.progress + "%"
    if (progress.phase) {
      switch (progress.phase) {
        case "sending":
          displayText = progress.progress + "% - Chunks senden"
          break
        case "combining":
          displayText = progress.progress + "% - Zusammenfügen"
          break
        case "encrypting":
          displayText = progress.progress + "% - Verschlüsseln"
          break
        case "uploading":
          displayText = progress.progress + "% - Cloud Upload"
          break
        case "finalizing":
          displayText = progress.progress + "% - Abschluss"
          break
        default:
          displayText = progress.progress + "% - " + progress.phase
      }
    }

    debugLog("🔥 UI UPDATE:", displayText)
    progressText.textContent = displayText
    updateProgressRing(progress.progress)

    if (progress.status === "completed") {
      card.className = "upload-status-card success"
      progressText.textContent = "✅ Abgeschlossen"
      updateProgressRing(100)
    } else if (progress.status === "error") {
      card.className = "upload-status-card error"
      progressText.textContent = "❌ Fehler"
    }
  }

  function showUploadStatusCardError(file, errorMessage) {
    var card = document.getElementById("uploadStatusCard")
    var progressText = document.getElementById("uploadStatusProgress")
    if (card && progressText) {
      card.className = "upload-status-card error"
      progressText.textContent = "❌ " + (errorMessage || "Fehler")
    }
  }

  function hideUploadStatusCard() {
    var card = document.getElementById("uploadStatusCard")
    if (card && card.parentNode) {
      card.parentNode.removeChild(card)
      currentUploadCard = null
      debugLog("Upload status card removed")
    }
  }

  function updateProgressRing(progress) {
    var circle = document.getElementById("progressCircle")
    if (!circle) return
    var circumference = 2 * Math.PI * 10 // radius = 10
    var offset = circumference - (progress / 100) * circumference
    circle.style.strokeDashoffset = offset
  }

  function showMessage(type, text) {
    debugLog("Showing message:", type, text)
    // Create message content
    var messageContent = document.createElement("div")
    messageContent.className = "message-content"
    messageContent.textContent = text

    // Create close button
    var closeButton = document.createElement("div")
    closeButton.className = "message-close"
    closeButton.textContent = "×"
    closeButton.title = "Schließen"
    closeButton.onclick = () => {
      hideMessage()
    }

    // Clear and populate message box
    messageBox.innerHTML = ""
    messageBox.appendChild(messageContent)
    messageBox.appendChild(closeButton)
    messageBox.className = type + " visible"

    setTimeout(() => {
      try {
        messageBox.scrollIntoView({ behavior: "smooth", block: "nearest" })
      } catch (e) {
        // Fallback for older browsers
        messageBox.scrollIntoView()
      }
    }, 100)
  }

  function hideMessage() {
    debugLog("Hiding message")
    messageBox.className = ""
    messageBox.innerHTML = ""
  }

  // Zeigt den fertigen E-Mail-Text als Backup an, damit er notfalls manuell kopiert werden kann
  function showMailFallback(html) {
    if (!mailFallback || !mailFallbackContent) return
    try {
      mailFallbackContent.innerHTML = html
    } catch (e) {
      mailFallbackContent.textContent = html
    }
    mailFallback.style.display = "block"
  }

  function hideMailFallback() {
    if (!mailFallback) return
    mailFallback.style.display = "none"
    if (mailFallbackContent) mailFallbackContent.innerHTML = ""
  }

  // Kopiert den formatierten Backup-Text in die Zwischenablage (robust für alte und neue WebViews)
  function handleCopyMailText() {
    if (!mailFallbackContent) return
    var copied = false
    // 1) Rich-Copy über Auswahl + execCommand (behält Link/Formatierung, läuft auch in alten WebViews)
    try {
      var range = document.createRange()
      range.selectNodeContents(mailFallbackContent)
      var sel = window.getSelection()
      sel.removeAllRanges()
      sel.addRange(range)
      copied = document.execCommand("copy")
      sel.removeAllRanges()
    } catch (e) {
      copied = false
    }

    // 2) Fallback: moderne Clipboard-API mit reinem Text
    if (!copied && navigator.clipboard && navigator.clipboard.writeText) {
      var plain = mailFallbackContent.innerText || mailFallbackContent.textContent || ""
      navigator.clipboard
        .writeText(plain)
        .then(function () {
          flashCopyButton(true)
        })
        .catch(function () {
          flashCopyButton(false)
        })
      return
    }

    flashCopyButton(copied)
  }

  function flashCopyButton(success) {
    if (!copyMailText) return
    var original = "Text kopieren"
    copyMailText.textContent = success ? "Kopiert!" : "Bitte manuell markieren"
    setTimeout(function () {
      copyMailText.textContent = original
    }, 2000)
  }

  if (copyMailText) {
    copyMailText.onclick = handleCopyMailText
  }

  // Event listeners
  debugLog("Adding file upload event listeners")

  // Use onclick instead of addEventListener for better compatibility
  uploadArea.onclick = () => {
    debugLog("Upload area clicked")
    fileInput.click()
  }

  uploadArea.ondragover = (e) => {
    e.preventDefault()
    uploadArea.style.borderColor = "var(--black)"
    uploadArea.style.backgroundColor = "var(--gray-50)"
  }

  uploadArea.ondragleave = () => {
    uploadArea.style.borderColor = "var(--gray-300)"
    uploadArea.style.backgroundColor = "var(--white)"
  }

  uploadArea.ondrop = (e) => {
    debugLog("Files dropped")
    e.preventDefault()
    uploadArea.style.borderColor = "var(--gray-300)"
    uploadArea.style.backgroundColor = "var(--white)"
    try {
      var items = e.dataTransfer && e.dataTransfer.items
      debugLog("Drop: has items:", !!items, items ? items.length : 0)
      if (items && items.length && items[0] && typeof items[0].webkitGetAsEntry === "function") {
        debugLog("Drop: using webkitGetAsEntry path")
        // Ordner- oder Datei-Drop über Entry-API (Chrome/Edge)
        getFilesFromDataTransferItems(items)
          .then(function (files) {
            debugLog("Drop: entries resolved, file count:", files ? files.length : 0)
            if (files && files.length) {
              appendFiles(files)
            } else if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
              debugLog("Drop: fallback to dataTransfer.files (non-empty)")
              appendFiles(e.dataTransfer.files)
            } else {
              debugLog("Drop: no files discovered from entries or files")
              tryOpenFolderPickerFallback()
            }
          })
          .catch(function (err) {
            debugLog("Drop: entry parsing failed, fallback to files", err && err.message ? err.message : err)
            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
              appendFiles(e.dataTransfer.files)
            } else {
              tryOpenFolderPickerFallback()
            }
          })
      } else if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        // Fallback: normale Dateien
        debugLog("Drop: using dataTransfer.files path")
        debugLog("Drop: dataTransfer.files length:", e.dataTransfer.files.length)
        appendFiles(e.dataTransfer.files)
      } else {
        // Weder items noch files vorhanden → Runtime unterstützt Ordner-DnD nicht
        debugLog("Drop: neither items nor files present – likely no folder DnD support in this WebView")
        tryOpenFolderPickerFallback()
      }
    } catch (err) {
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        debugLog("Drop: exception, fallback to dataTransfer.files")
        appendFiles(e.dataTransfer.files)
      } else {
        tryOpenFolderPickerFallback()
      }
    }
  }

  function tryOpenFolderPickerFallback() {
    try {
      var supportsFSApi = typeof window.showDirectoryPicker === 'function'
      var supportsFolderPickerAttr = false
      try {
        var tmp = document.createElement('input')
        tmp.type = 'file'
        supportsFolderPickerAttr = ('webkitdirectory' in tmp) || ('directory' in tmp)
      } catch (e) {}
      if (supportsFSApi) {
        debugLog('Drop: opening directory picker (FS Access API) as fallback')
        pickDirectoryAndAppend()
        return
      }
      if (supportsFolderPickerAttr && folderInput) {
        debugLog('Drop: opening hidden folder input as fallback')
        try { folderInput.click() } catch (e) {}
        return
      }
      showMessage('error', 'Ordner per Drag & Drop wird hier nicht unterstützt. Bitte wählen Sie die Dateien manuell aus oder komprimieren Sie den Ordner als ZIP.')
    } catch (e) {}
  }

  fileInput.onchange = (e) => {
    debugLog("File input changed, files:", e.target.files.length)
    appendFiles(e.target.files)
    fileInput.value = "" // Reset the input
  }

  if (folderSelectLink && folderInput) {
    // Feature-Detection: Unterstützt die Runtime Ordnerauswahl?
    var supportsFolderPickerAttr = false
    try {
      var tmp = document.createElement('input')
      tmp.type = 'file'
      supportsFolderPickerAttr = ('webkitdirectory' in tmp) || ('directory' in tmp)
    } catch (e) {}

    var supportsFSApi = typeof window.showDirectoryPicker === 'function'

    // UI anpassen, wenn NICHT unterstützt
    if (!supportsFolderPickerAttr && !supportsFSApi) {
      if (uploadSubtextEl) {
        uploadSubtextEl.innerHTML = 'Unterstützt große Dateien'
      }
    }

    folderSelectLink.onclick = async function (ev) {
      ev.preventDefault()
      // 1) Moderner Weg: File System Access API
      if (supportsFSApi) {
        try {
          var ok = await pickDirectoryAndAppend()
          if (ok) return
        } catch (e) {
          debugLog('Directory picker failed:', e && e.message ? e.message : e)
        }
      }
      // 2) Fallback: verstecktes Input mit webkitdirectory
      if (supportsFolderPickerAttr) {
        try { folderInput.click() } catch (e) { debugLog('Folder input click failed') }
        return
      }
      // 3) Kein Support
      showMessage('error', 'Ordnerauswahl wird in dieser Outlook-/Browser-Version nicht unterstützt. Bitte nutzen Sie Drag & Drop für Ordner (falls verfügbar) oder wählen Sie die Dateien einzeln.')
    }
    folderInput.onchange = function (e) {
      var fl = e && e.target && e.target.files ? e.target.files : null
      debugLog("Folder input changed, files:", fl ? fl.length : 0)
      if (fl && fl.length) appendFiles(fl)
      folderInput.value = ""
    }
  }

  // FS Access API: Ordner rekursiv einlesen
  async function pickDirectoryAndAppend() {
    if (typeof window.showDirectoryPicker !== 'function') return false
    try {
      var dir = await window.showDirectoryPicker()
      var files = []
      await collectFilesFromDirectoryHandle(dir, '', files)
      if (files.length) {
        appendFiles(files)
        return true
      }
      return false
    } catch (e) {
      return false
    }
  }

  async function collectFilesFromDirectoryHandle(dirHandle, path, files) {
    try {
      for await (var _entry of dirHandle.entries()) {
        var name = _entry[0]
        var handle = _entry[1]
        if (handle.kind === 'file') {
          var f = await handle.getFile()
          try { Object.defineProperty(f, 'webkitRelativePath', { value: path + name }) } catch (e) {}
          files.push(f)
        } else if (handle.kind === 'directory') {
          await collectFilesFromDirectoryHandle(handle, path + name + '/', files)
        }
      }
    } catch (e) {}
  }

  uploadButton.onclick = handleUpload

  function handleUpload() {
    debugLog("Upload button clicked")
    hideMessage() // Clear any existing messages
    hideMailFallback() // Alten Backup-Text zurücksetzen

    if (allFiles.length === 0) {
      showMessage("error", "Bitte wählen Sie eine oder mehrere Dateien aus.")
      return
    }

    // Alle Dateien sollten bereits hochgeladen sein (chunked upload)
    if (!blobUploadManager || !allFiles.every((file) => file.isUploaded)) {
      showMessage("error", "Bitte warten Sie, bis alle Dateien hochgeladen sind.")
      return
    }

    // Erstelle Message-Metadaten und füge in E-Mail ein
    createMessageAndInsert()
  }

  // NEUE FUNKTION: Message-Metadaten für chunked uploads erstellen
  function createMessageAndInsert() {
    debugLog("Creating message metadata for chunked uploads...")
    uploadButton.disabled = true
    uploadButton.innerHTML = '<span class="loading-spinner"></span>Erstelle Download-Informationen...'

    try {
      var sender = window.Office.context.mailbox.userProfile.emailAddress
      
      // Hilfsfunktion: Empfänger aus verschiedenen Quellen sammeln
      function getRecipients(callback) {
        var recipientsList = []
        var item = window.Office.context.mailbox.item
        
        // Prüfe, ob item.to existiert und getAsync hat (neue API)
        if (item.to && typeof item.to.getAsync === 'function') {
          item.to.getAsync(function(res) {
            if (res.status === window.Office.AsyncResultStatus.Succeeded && res.value) {
              for (var i = 0; i < res.value.length; i++) {
                if (res.value[i].emailAddress) {
                  recipientsList.push(res.value[i].emailAddress)
                }
              }
            }
            // Auch CC prüfen (falls verfügbar)
            if (item.cc && typeof item.cc.getAsync === 'function') {
              item.cc.getAsync(function(ccRes) {
                if (ccRes.status === window.Office.AsyncResultStatus.Succeeded && ccRes.value) {
                  for (var j = 0; j < ccRes.value.length; j++) {
                    if (ccRes.value[j].emailAddress) {
                      recipientsList.push(ccRes.value[j].emailAddress)
                    }
                  }
                }
                callback(recipientsList)
              })
            } else {
              // CC nicht verfügbar oder kein getAsync - direkt callback aufrufen
              callback(recipientsList)
            }
          })
        } else if (item.to && Array.isArray(item.to)) {
          // Fallback: item.to ist bereits ein Array (ältere API)
          for (var k = 0; k < item.to.length; k++) {
            if (item.to[k].emailAddress) {
              recipientsList.push(item.to[k].emailAddress)
            }
          }
          if (item.cc && Array.isArray(item.cc)) {
            for (var l = 0; l < item.cc.length; l++) {
              if (item.cc[l].emailAddress) {
                recipientsList.push(item.cc[l].emailAddress)
              }
            }
          }
          callback(recipientsList)
        } else {
          // Keine Empfänger verfügbar - leeres Array zurückgeben
          debugLog("No recipients found in item.to")
          callback([])
        }
      }
      
      // Get recipients
      getRecipients(function(recipientsList) {
        if (recipientsList.length === 0) {
          showMessage("error", "Bitte fügen Sie mindestens einen Empfänger zur E-Mail hinzu.")
          uploadButton.disabled = false
          uploadButton.innerHTML = "Dateien hochladen & einfügen"
          return
        }
        
        var recipients = recipientsList.join(",")

        // Prepare attachmentUrls for API (Format: Array mit url, name, folder)
        var attachmentUrls = []
        for (var j = 0; j < allFiles.length; j++) {
          if (allFiles[j].isUploaded && allFiles[j].blobUrl) {
            var attachment = {
              url: allFiles[j].blobUrl,
              name: allFiles[j].name,
            }
            if (allFiles[j].folderLabel && allFiles[j].folderLabel.trim().length > 0) {
              attachment.folder = allFiles[j].folderLabel
            }
            attachmentUrls.push(attachment)
          }
        }

        debugLog("Creating message metadata for", attachmentUrls.length, "files")

        // Use main backend API endpoint (anolink-backend)
        var backendUrl = getBackendUrl()
        var createMessageUrl = backendUrl + "/createMessage"
        debugLog("Calling createMessage at:", createMessageUrl)

        // Auth-Header aus localStorage (anolink_token/anolink_email). Das Backend
        // erwartet unter "x-client-type: outlook-addin" die zwei dedizierten
        // Header x-addin-email + x-addin-token; NICHT mehr "Authorization: Bearer".
        // Siehe anolink-backend/src/auth/requireAuth.ts → authenticateAddin.
        var addinToken = null
        var addinEmail = null
        try {
          addinToken = window.localStorage && window.localStorage.getItem("anolink_token")
          addinEmail = window.localStorage && window.localStorage.getItem("anolink_email")
        } catch (e) {}

        var headers = {
          "Content-Type": "application/json",
          "X-Client-Type": "outlook-addin",
        }
        if (addinEmail) headers["x-addin-email"] = addinEmail
        if (addinToken) headers["x-addin-token"] = addinToken

        var requestBody = {
          recipient: recipients,
          subject: "Ihre Unterlagen",
          message: "Im Auftrag von " + sender + " stellen wir Ihnen die folgenden Unterlagen zur Verfügung.",
          sender: sender,
          attachmentUrls: attachmentUrls,
        }
        debugLog("createMessage request body:", JSON.stringify(requestBody))

        fetch(createMessageUrl, {
          method: "POST",
          headers: headers,
          body: JSON.stringify(requestBody),
        })
          .then(async (response) => {
            debugLog("Create message response status:", response.status, "URL:", createMessageUrl)
            var data = null
            try {
              data = await response.json()
            } catch (parseErr) {
              debugLog("JSON parse error:", parseErr, "Status:", response.status)
              if (response.status === 404) {
                throw new Error("API-Endpoint nicht gefunden (404). Route: " + createMessageUrl)
              }
              throw new Error("Ungültige Serverantwort. Status: " + response.status + ", URL: " + createMessageUrl)
            }
            if (!response.ok) {
              var errorMsg = data && data.error ? data.error : "Message-Erstellung fehlgeschlagen"
              debugLog("Create message failed:", errorMsg, "Status:", response.status)
              throw new Error(errorMsg + " (Status: " + response.status + ")")
            }
            return data
          })
          .then((data) => {
            debugLog("Message metadata created:", JSON.stringify(data))
            if (data && data.success) {
              // Update allFiles with download info (neue API gibt messageId statt uniqueId)
              for (var k = 0; k < allFiles.length; k++) {
                if (allFiles[k].isUploaded) {
                  allFiles[k].externalId = data.externalId
                  allFiles[k].externalPassword = data.externalPassword
                  allFiles[k].uniqueId = data.messageId // Neue API verwendet messageId
                }
              }
              // Konvertiere Response für insertFilesIntoEmail (wartet auf uniqueId, externalId, externalPassword)
              var convertedData = {
                uniqueId: data.messageId,
                externalId: data.externalId,
                externalPassword: data.externalPassword,
              }
              insertFilesIntoEmail(convertedData)
            } else {
              throw new Error(data.error || "Failed to create message metadata")
            }
          })
          .catch((err) => {
            debugLog("Create message error:", err)
            showMessage("error", "Fehler beim Erstellen der Download-Informationen: " + err.message)
            uploadButton.disabled = false
            uploadButton.innerHTML = "Dateien hochladen & einfügen"
          })
      })
    } catch (err) {
      debugLog("Create message error:", err)
      showMessage("error", "Fehler: " + err.message)
      uploadButton.disabled = false
      uploadButton.innerHTML = "Dateien hochladen & einfügen"
    }
  }

  function insertFilesIntoEmail(data) {
    debugLog("Inserting files into email...")
    uploadButton.innerHTML = '<span class="loading-spinner"></span>Einfügen in E-Mail...'

    try {
      var sender = window.Office.context.mailbox.userProfile.emailAddress
      // Get recipients to check for KIM addresses
      window.Office.context.mailbox.item.to.getAsync((res) => {
        if (res.status !== window.Office.AsyncResultStatus.Succeeded) {
          showMessage("error", "Empfänger konnte nicht abgerufen werden.")
          uploadButton.disabled = false
          uploadButton.innerHTML = "Dateien hochladen & einfügen"
          return
        }

        var recipientsList = []
        for (var i = 0; i < res.value.length; i++) {
          recipientsList.push(res.value[i].emailAddress)
        }

        // Check if any recipient contains "kim.telematik"
        var hasKimTelematik = false
        for (var j = 0; j < recipientsList.length; j++) {
          if (recipientsList[j].toLowerCase().indexOf("kim.telematik") !== -1) {
            hasKimTelematik = true
            break
          }
        }

        // Sammle Download-Informationen - NUR ID, KEIN PASSWORT
        var downloadInfo = ""
        var hasDownloadInfo = false
        for (var k = 0; k < allFiles.length; k++) {
          if (allFiles[k].externalId) {
            // Nur die Download-ID anzeigen, kein Passwort
            downloadInfo += `<p><strong>Download-ID:</strong> ${allFiles[k].externalId}</p>`
            hasDownloadInfo = true
            break // Nur eine Download-Info anzeigen
          }
        }

        var emailHTML
        var normalizedSender = (sender || "").toLowerCase()
        var useFirewallFriendlyTemplate =
          normalizedSender.endsWith("@agaplesion.de") || normalizedSender === "fabian@anonetics.de"

        if (useFirewallFriendlyTemplate) {
          // Firewallfreundliches Klartext-Template ohne Links/Buttons/auffaellige Formatierungen
          emailHTML =
            '<div style="font-family: Arial, sans-serif; font-size: 14px; line-height: 1.35; color: #000;">' +
            "Weitere Unterlagen wurden Ihnen über anolink zur Verfügung gestellt.<br>" +
            "Es handelt sich um ein verschlüsseltes Dateiportal, das die Sicherheit Ihrer Daten gewährleistet.<br><br>" +
            "Kurzanleitung:<br>" +
            "1. Besuchen Sie das Portal: portal.anolink.de<br>" +
            "2. Geben Sie die Download-ID " +
            (allFiles[0] && allFiles[0].externalId ? allFiles[0].externalId : "XYZ") +
            " ein<br>" +
            "3. Laden Sie Ihre Dateien herunter<br><br>" +
            "Ihre Dateien:<br>"

          for (var l = 0; l < allFiles.length; l++) {
            emailHTML += allFiles[l].name + " (" + formatSize(allFiles[l].size) + ")<br>"
          }

          emailHTML += "</div>"
        } else if (hasKimTelematik) {
          // KIM-Telematik E-Mail Template
          emailHTML =
            '<div style="font-family: Arial, sans-serif; font-size: 14px; color: #000000; line-height: 1.6; max-width: 600px;">' +
            '<p style="margin: 0 0 24px 0; font-size: 16px;"><strong>Weitere Unterlagen wurden Ihnen über anolink zur Verfügung gestellt.</strong></p>' +
            // Kurzanleitung oben
            '<div style="margin-bottom: 24px;">' +
            '<p style="margin: 0 0 12px 0; font-weight: 600; font-size: 15px;">Kurzanleitung:</p>' +
            '<div style="margin-left: 0; padding-left: 20px;">' +
            '<p style="margin: 8px 0; padding-left: 0;"><strong>1. </strong>Besuchen Sie das Portal: <a href="https://link.anolink.de" target="_blank" style="color: #0066cc;">https://link.anolink.de</a></p>' +
            '<p style="margin: 8px 0; padding-left: 0;"><strong>2. </strong>Geben Sie die Download-ID <strong>' +
            (allFiles[0] && allFiles[0].externalId ? allFiles[0].externalId : "XYZ") +
            "</strong> ein</p>" +
            '<p style="margin: 8px 0; padding-left: 0;"><strong>3. </strong>Laden Sie Ihre Dateien herunter</p>' +
            "</div>" +
            "</div>" +
            // Button
            '<div style="text-align: center; margin: 24px 0;">' +
            '<table cellpadding="0" cellspacing="0" border="0" style="margin: 0 auto;">' +
            "<tr>" +
            '<td style="background-color:#000000;color:#FFFFFF;font-size:14px;padding:12px 24px;border-radius:4px;font-family:Arial, sans-serif;text-align:center;">' +
            '<a href="https://link.anolink.de" style="color:#FFFFFF;text-decoration:none;font-weight:600;">Dateien abrufen</a>' +
            "</td>" +
            "</tr>" +
            "</table>" +
            "</div>" +
            '<p style="font-size: 12px; color: #6b7280; margin-top: 24px;">Die Dateien wurden sicher verschlüsselt übertragen.</p>' +
            "</div>"
        } else {
          // Standard E-Mail Template - Kurzanleitung
          emailHTML =
            '<div style="font-family: Arial, sans-serif; font-size: 14px; color: #000000; line-height: 1.6; max-width: 600px;">' +
            '<p style="margin: 0 0 24px 0; font-size: 16px;"><strong>Weitere Unterlagen wurden Ihnen über anolink zur Verfügung gestellt.</strong></p>' +
            // Kurzanleitung oben - mit Box aber ohne grauen Hintergrund
            '<div style="background-color: #ffffff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 20px; margin-bottom: 24px;">' +
            '<p style="margin: 0 0 16px 0; font-weight: 600; font-size: 15px; color: #000;">Kurzanleitung:</p>' +
            '<div style="margin-left: 0;">' +
            '<p style="margin: 10px 0; padding-left: 0; display: flex; align-items: flex-start;"><span style="font-weight: 600; margin-right: 8px; min-width: 20px;">1. </span><span>Besuchen Sie das Portal: <a href="https://link.anolink.de" target="_blank" style="color: #0066cc; text-decoration: underline;">https://link.anolink.de</a></span></p>' +
            '<p style="margin: 10px 0; padding-left: 0; display: flex; align-items: flex-start;"><span style="font-weight: 600; margin-right: 8px; min-width: 20px;">2. </span><span>Geben Sie die Download-ID <strong>' +
            (allFiles[0] && allFiles[0].externalId ? allFiles[0].externalId : "XYZ") +
            "</strong> ein</span></p>" +
            '<p style="margin: 10px 0; padding-left: 0; display: flex; align-items: flex-start;"><span style="font-weight: 600; margin-right: 8px; min-width: 20px;">3. </span><span>Laden Sie Ihre Dateien herunter</span></p>' +
            "</div>" +
            "</div>" +
            // Dateiliste
            '<div style="background-color: #ffffff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 20px; margin-bottom: 20px;">' +
            '<p style="margin: 0 0 12px 0; font-weight: 600; color: #000;">Ihre Dateien:</p>' +
            '<div style="margin: 0; padding-left: 0;">'

          // Add file list with better styling
          for (var l = 0; l < allFiles.length; l++) {
            emailHTML +=
              '<div style="margin-bottom: 6px; color: #374151; padding-left: 0;">' +
              allFiles[l].name +
              ' <span style="color: #6b7280;">(' +
              formatSize(allFiles[l].size) +
              ")</span></div>"
          }

          emailHTML +=
            "</div>" +
            "</div>" + // Schließt die Dateiliste-Box
            // Button - zentriert und schöner
            '<div style="text-align: center; margin: 32px 0;">' +
            '<table cellpadding="0" cellspacing="0" border="0" style="margin: 0 auto;">' +
            "<tr>" +
            '<td style="background-color:#000000;color:#FFFFFF;font-size:15px;padding:14px 32px;border-radius:6px;font-family:Arial, sans-serif;text-align:center;box-shadow: 0 2px 4px rgba(0,0,0,0.1);">' +
            '<a href="https://link.anolink.de" style="color:#FFFFFF;text-decoration:none;font-weight:600;">Dateien abrufen</a>' +
            "</td>" +
            "</tr>" +
            "</table>" +
            "</div>" +
            '<p style="font-size: 12px; color: #6b7280; text-align: center; margin-top: 24px; font-style: italic;">Die Dateien wurden sicher verschlüsselt übertragen.</p>' +
            "</div>"
        }

        debugLog("Inserting email HTML...")
        // Backup: E-Mail-Text immer zum Kopieren bereitstellen, bevor eingefügt wird
        showMailFallback(emailHTML)

        window.Office.context.mailbox.item.body.setSelectedDataAsync(emailHTML, { coercionType: "html" }, (result) => {
          debugLog("Insert result:", result.status)
          if (result.status === window.Office.AsyncResultStatus.Succeeded) {
            showMessage("success", "Dateien erfolgreich hochgeladen und Link eingefügt. Der Text steht unten zusätzlich zum Kopieren bereit.")
            allFiles = []
            renderFiles()
          } else {
            showMessage(
              "error",
              "Der Text konnte nicht automatisch eingefügt werden. Bitte kopieren Sie ihn unten und fügen Sie ihn manuell in die E-Mail ein."
            )
            try {
              mailFallback.scrollIntoView({ behavior: "smooth", block: "nearest" })
            } catch (e) {
              try {
                mailFallback.scrollIntoView()
              } catch (e2) {}
            }
          }
          uploadButton.disabled = false
          uploadButton.innerHTML = "Dateien hochladen & einfügen"
        })
      })
    } catch (err) {
      debugLog("Insert error:", err)
      showMessage("error", "Fehler beim Einfügen: " + err.message)
      uploadButton.disabled = false
      uploadButton.innerHTML = "Dateien hochladen & einfügen"
    }
  }

  debugLog("File upload initialization complete")
}

// Declare formatSize function
function formatSize(bytes) {
  if (bytes === 0) return "0 Bytes"
  var k = 1024
  var sizes = ["Bytes", "KB", "MB", "GB"]
  var i = Math.floor(Math.log(bytes) / Math.log(k))
  return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
}

// Add a debug div to the page if in debug mode
if (debugMode) {
  document.addEventListener("DOMContentLoaded", () => {
    var debugDiv = document.createElement("div")
    debugDiv.id = "debug-output"
    debugDiv.style.cssText =
      "position: fixed; bottom: 0; left: 0; right: 0; " +
      "height: 100px; background: #f0f0f0; border-top: 1px solid #ccc; " +
      "overflow-y: auto; font-family: monospace; font-size: 10px; padding: 5px; " +
      "z-index: 9999; display: none;" // Hidden by default

    var debugToggle = document.createElement("button")
    debugToggle.textContent = "Debug"
    debugToggle.style.cssText =
      "position: fixed; bottom: 0; right: 0; z-index: 10000; " +
      "background: #333; color: white; border: none; padding: 5px; " +
      "font-size: 10px; cursor: pointer;"

    debugToggle.onclick = () => {
      if (debugDiv.style.display === "none") {
        debugDiv.style.display = "block"
      } else {
        debugDiv.style.display = "none"
      }
    }

    document.body.appendChild(debugDiv)
    document.body.appendChild(debugToggle)
  })
}

